/**
 * Avatar / welcome-thumb image variants. Profile photos often only have
 * square-* ; listing covers often only have listing-card / default.
 */

export const AVATAR_IMAGE_VARIANTS = [
  'square-xsmall',
  'square-xsmall2x',
  'square-small',
  'square-small2x',
  'listing-card',
  'listing-card-2x',
  'default',
  'scaled-small',
  'scaled-medium',
];

/** Hero / carousel first, then card and avatar crops. */
export const HERO_IMAGE_VARIANTS = [
  'scaled-small',
  'scaled-medium',
  'scaled-large',
  'scaled-xlarge',
  'listing-card',
  'listing-card-2x',
  'default',
  'square-small',
  'square-small2x',
  'square-xsmall',
  'square-xsmall2x',
];

/**
 * Marketplace API `fields.image` list so listing, dashboard, menu, and Inbox
 * all receive every variant a photo might actually have.
 */
export const IMAGE_FIELD_VARIANTS = HERO_IMAGE_VARIANTS.map(name => `variants.${name}`);

/**
 * Variant names on an image entity that have a usable URL.
 *
 * @param {Object} [image]
 * @returns {string[]}
 */
export const usableImageVariantNames = image => {
  const variants = image?.attributes?.variants;
  if (!variants || typeof variants !== 'object') {
    return [];
  }
  return Object.keys(variants).filter(name => {
    const url = variants[name]?.url;
    return typeof url === 'string' && url.length > 0;
  });
};

/**
 * Prefer the requested names that have URLs, then any URL the image has.
 *
 * @param {Object} [image]
 * @param {string[]} [preferred]
 * @returns {string[]}
 */
export const resolveImageVariants = (image, preferred = []) => {
  const available = usableImageVariantNames(image);
  const wanted = (Array.isArray(preferred) ? preferred : []).filter(name =>
    available.includes(name)
  );
  return wanted.length ? wanted : available;
};

/**
 * Prefer square / card variants, then any URL the image actually has.
 *
 * @param {Object} [image]
 * @returns {string[]}
 */
export const avatarImageVariants = image => resolveImageVariants(image, AVATAR_IMAGE_VARIANTS);

/**
 * True when the image can render in Avatar / ResponsiveImage.
 *
 * @param {Object} [image]
 * @returns {boolean}
 */
export const hasUsableAvatarImage = image => avatarImageVariants(image).length > 0;
